// pages/ChatSpace/ChartRenderer.js
import React, { useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { Box, Typography, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { jsPDF } from 'jspdf';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const ChartRenderer = ({ config }) => {
    const chartRef = useRef(null);

    if (!config || !config.type || !config.data) {
        return (
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="error">
                    Invalid chart configuration
                </Typography>
            </Box>
        );
    }

    const { type, data, options = {} } = config;

    // Get chart title for filename
    const chartTitle = options?.plugins?.title?.text || data?.datasets?.[0]?.label || `${type}-chart`;
    const safeFileName = chartTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();

    const defaultOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        ...options,
    };

    const chartProps = {
        data,
        options: defaultOptions,
    };

    // Download as PNG image (HD quality)
    const handleDownloadImage = () => {
        if (chartRef.current) {
            const canvas = chartRef.current.canvas;

            // Create a high-resolution canvas
            const scaleFactor = 3; // 3x for HD quality
            const hdCanvas = document.createElement('canvas');
            hdCanvas.width = canvas.width * scaleFactor;
            hdCanvas.height = canvas.height * scaleFactor;

            const ctx = hdCanvas.getContext('2d');
            ctx.scale(scaleFactor, scaleFactor);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(canvas, 0, 0);

            const link = document.createElement('a');
            link.download = `${safeFileName}_${Date.now()}.png`;
            link.href = hdCanvas.toDataURL('image/png', 1.0);
            link.click();
        }
    };

    // Download as PDF (HD quality)
    const handleDownloadPDF = () => {
        if (chartRef.current) {
            const canvas = chartRef.current.canvas;

            // Create a high-resolution canvas for better PDF quality
            const scaleFactor = 3;
            const hdCanvas = document.createElement('canvas');
            hdCanvas.width = canvas.width * scaleFactor;
            hdCanvas.height = canvas.height * scaleFactor;

            const ctx = hdCanvas.getContext('2d');
            ctx.scale(scaleFactor, scaleFactor);
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(canvas, 0, 0);

            const imgData = hdCanvas.toDataURL('image/png', 1.0);

            // A4 dimensions in points
            const a4Width = 595.28;
            const a4Height = 841.89;
            const margin = 40;
            const contentWidth = a4Width - (margin * 2);

            // Calculate scaled dimensions to fit A4 width
            const imgAspectRatio = canvas.height / canvas.width;
            const scaledWidth = contentWidth;
            const scaledHeight = contentWidth * imgAspectRatio;

            // Create PDF
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: 'a4',
                compress: true,
            });

            // Add title
            pdf.setFontSize(18);
            pdf.setTextColor(51, 51, 51);
            pdf.text(chartTitle, margin, 40);

            // Add timestamp
            pdf.setFontSize(10);
            pdf.setTextColor(128, 128, 128);
            pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 60);

            // Add line separator
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.5);
            pdf.line(margin, 70, a4Width - margin, 70);

            // Add chart image centered with proper sizing
            pdf.addImage(imgData, 'PNG', margin, 90, scaledWidth, scaledHeight, undefined, 'FAST');

            pdf.save(`${safeFileName}_${Date.now()}.pdf`);
        }
    };

    const renderChart = () => {
        const commonProps = { ...chartProps, ref: chartRef };
        switch (type.toLowerCase()) {
            case 'bar':
                return <Bar {...commonProps} />;
            case 'line':
                return <Line {...commonProps} />;
            case 'pie':
                return <Pie {...commonProps} />;
            case 'doughnut':
                return <Doughnut {...commonProps} />;
            default:
                return <Bar {...commonProps} />;
        }
    };

    return (
        <Box
            sx={{
                my: 2,
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                maxWidth: 500, // Reduced from 600 to fit better in PDF
                width: '100%',
                position: 'relative',
                '&:hover .chart-download-buttons': {
                    opacity: 1,
                },
            }}
        >
            {/* Download buttons - appear on hover */}
            <Box
                className="chart-download-buttons"
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 0.5,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: 1,
                    p: 0.5,
                    zIndex: 10,
                }}
            >
                <MuiTooltip title="Download as PNG (HD)">
                    <IconButton
                        size="small"
                        onClick={handleDownloadImage}
                        sx={{
                            bgcolor: 'grey.100',
                            '&:hover': { bgcolor: 'grey.200' },
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </MuiTooltip>
                <MuiTooltip title="Download as PDF (HD)">
                    <IconButton
                        size="small"
                        onClick={handleDownloadPDF}
                        sx={{
                            bgcolor: 'error.light',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.main' },
                        }}
                    >
                        <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </MuiTooltip>
            </Box>
            {renderChart()}
        </Box>
    );
};

export default ChartRenderer;
